/* eslint-disable react-refresh/only-export-components */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { PageContainer } from '@/components/shared/PageContainer';
import { EmptyState } from '@/components/ui/empty-state';
import { useBreadcrumbLabel } from '@/hooks/useBreadcrumbLabel';
import { useTranslator } from '@/hooks/useTranslator';
import { ActivateTemplateDialog } from '../../../-components/ActivateTemplateDialog';
import { DeleteScoringTemplatesDialog } from '../../../-components/DeleteScoringTemplatesDialog';
import { MethodologyDialog } from '../../../-components/MethodologyDialog';
import { ScoringFindingsTable } from '../../../-components/ScoringFindingsTable';
import { ScoringTemplateHeaderCard } from '../../../-components/ScoringTemplateHeaderCard';
import { SplitFindingDialog } from '../../../-components/SplitFindingDialog';
import { useMethodology } from '../../../-hooks/useMethodology';
import { useScoringTemplateEditor } from '../../../-hooks/useScoringTemplateEditor';

export const Route = createFileRoute(
  '/management/scoring-templates/(pages)/$id/edit/',
)({ component: ScoringTemplateEditorRoute });

const PAGE_SIZE = 50;

function ScoringTemplateEditorRoute() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'scoringTemplates' });

  // Paging and search are the page's own UI state and go to the server as
  // query params — 799 rows is not a list to filter in the browser.
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [search, setSearch] = useState('');
  const [isMethodologyOpen, setMethodologyOpen] = useState(false);
  const methodology = useMethodology(id, isMethodologyOpen);

  const editor = useScoringTemplateEditor(id, {
    page: pagination.pageIndex + 1,
    count: pagination.pageSize,
    ...(search.trim() ? { search: search.trim() } : {}),
  });
  useBreadcrumbLabel(id, editor.template?.name);

  if (!editor.isLoading && !editor.template) {
    return (
      <PageContainer>
        <EmptyState title={t('editor.notFound')} />
      </PageContainer>
    );
  }

  /** A new search has no page 2 yet — staying on the old index would ask the
   *  server for a page the filtered set may not have and show an empty table. */
  function onSearchChange(next: string): void {
    setSearch(next);
    setPagination(prev => ({ ...prev, pageIndex: 0 }));
  }

  return (
    <PageContainer>
      <ScoringTemplateHeaderCard
        editor={editor}
        onOpenMethodology={() => setMethodologyOpen(true)}
      />
      <ScoringFindingsTable
        editor={editor}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        search={search}
        onSearchChange={onSearchChange}
      />
      <DeleteScoringTemplatesDialog
        pending={editor.pendingDelete}
        onCancel={editor.cancelDelete}
        onConfirm={() => void editor.confirmDelete()}
      />
      <ActivateTemplateDialog
        pending={editor.pendingActivate}
        onCancel={editor.cancelActivate}
        onConfirm={() => void editor.confirmActivate()}
      />
      <MethodologyDialog
        open={isMethodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        methodology={methodology}
      />
      <SplitFindingDialog
        pending={editor.pendingTextDecision}
        newFindingRef={editor.newFindingRef}
        onCancel={editor.cancelTextDecision}
        onKeepSame={editor.keepSameFinding}
        onChooseCreateNew={editor.chooseCreateNewFinding}
        onBack={editor.backToChoice}
        onCreateNew={editor.createNewFinding}
        onCodeChange={editor.setNewFindingRsscCode}
      />
    </PageContainer>
  );
}
